import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  alarmView,
  liveOf,
  chipView,
  connLine,
  gaugeReading,
  grindTip,
  historyCard,
  historyLanes,
  justBrewed,
  lastShotCard,
  liveReference,
  liveShotScreen,
  activityRing,
  ringShowsNote,
  machineNoteViews,
  readyTone,
  ringLine,
  ringTop,
  scheduleRow,
  scheduleSummaryParts,
  shotLine,
  t,
  tasteAfterTap,
  todayCard,
  usualCheck,
  usualText,
  type BrewcoreClient,
  type Lang,
} from '../core/index.js';
import {
  Collapse,
  Banner,
  Button,
  Card,
  CardHeader,
  ConnChip,
  FactRow,
  FitLine,
  formatNumber,
  Gauge,
  Iris,
  Icon,
  LaneChart,
  NavList,
  RateRow,
  Segmented,
  TopBar,
} from './components/index.js';
import { useMachine, useStore } from './machine-context.js';
import { RecoveryOffer } from './device/RecoveryOffer.js';
import { ActivityDial, AskSheet, FrameBanners, LiveShot, NoteRow } from './tabs/parts.js';
import { RecipeGlance } from './tabs/BrewTab.js';
// Claude after the shot, under the taste (Dominik 22.10.2026, board "Claude: nach dem Bezug")
import { setupOfferShown } from '../ai/view.js';
import { ClaudeBox, useClaudeCard } from './ai/ClaudeBox.js';
import { useAssistant, useOpenClaudeSettings } from './ai/use-assistant.js';

const READY_OR_HEATING_STATE = 10;

const num = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);

/**
 * Machine home ("Maschine" tab), 1:1 after the prototype canvas (boards "Maschine: heizt auf / bereit / Standby",
 * "Bezug läuft", "Bezug fertig", 21.10.2026): the ring with the state, the temperature and one line (when it will be
 * ready, when it goes into standby, when the schedule starts it), Standby and Spülen, the notes as cards; then, while
 * it is on, the next and the last shot; in standby the day so far and the schedule. During a shot the ring counts,
 * the steps and the curve follow; after it the weight, the rating and what to change. The last 20 minutes at the end
 * in the charts' look. Safety questions as alert sheets. Needs the MachineProvider around it.
 */
/** the fade of standby, switching on and a shot (components.css, data-swap): the fade and a frame */
const SWAP_MS = 480;

/**
 * A new number when the ring turns green after a warm-up (the display's readyPulse: pending while the heating screen
 * shows, more than 5 K below the setpoint; played once when it is ready), 0 before.
 */
function useReadyPulse(temp: number | undefined, target: number | undefined, tone: string): number {
  const pending = useRef(false);
  const [pulse, setPulse] = useState(0);
  useEffect(() => {
    if (tone === 'heat' && temp !== undefined && target !== undefined && temp < target - 5)
      pending.current = true;
    if (tone === 'ready' && pending.current) {
      pending.current = false;
      setPulse(Date.now());
    }
  }, [temp, target, tone]);
  return pulse;
}

export function MachineHome({
  client,
  machineId,
  lang,
  accountButton,
  switcher,
  onOpenRecipe,
  onOpenShot,
  onOpenHistory,
  onOpenSchedule,
}: {
  client: BrewcoreClient;
  machineId: string;
  lang: Lang;
  accountButton?: ReactNode;
  /** chips to switch machines (only with more than one) */
  switcher?: ReactNode;
  /** opens the recipe editor (Bezug tab) */
  onOpenRecipe?: () => void;
  /** opens a shot in the history (Bezug tab) */
  onOpenShot?: (shotId: string) => void;
  /** opens the history (Bezug tab) */
  onOpenHistory?: () => void;
  /** opens the schedule (Einstellungen) */
  onOpenSchedule?: () => void;
}) {
  const store = useMemo(() => client.watch(machineId), [client, machineId]);
  const view = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const commands = useSyncExternalStore(
    (fn) => client.commands.subscribe(fn),
    () => client.commands.openCommands(machineId).length,
  );
  const [confirming, setConfirming] = useState(false);
  const { ctx } = useMachine();
  const shots = useStore(client.shots(machineId));
  const liveShot = useStore(client.liveShot(machineId));
  const history = useStore(client.history(machineId));
  const beans = useSyncExternalStore(client.beans(machineId).subscribe, client.beans(machineId).getSnapshot);
  const ref = liveReference(shots, beans);
  useEffect(() => {
    if (ref.id && !ref.shot) void client.shots(machineId).curve(ref.id);
  }, [ref.id, ref.shot, client, machineId]);
  const shotScreen = liveShotScreen(ctx, liveShot, ref.shot);
  // water without a shot (the rinse after one, a flush, backflush, descaling): the ring shows its progress
  const activity = shotScreen ? null : activityRing(ctx);
  const alarm = alarmView(ctx);
  const notes = machineNoteViews(ctx);
  const last = lastShotCard(ctx, shots);
  const hist = historyCard(ctx, history);

  // calm over a weak link (core/calm.ts): short gaps keep the last values and the controls; only lasting ones show
  const controllable = ctx.controllable;
  const conn = connLine(ctx);
  // in standby the machine reports target 0; the ring keeps the recipe's setpoint as its scale
  const reading = gaugeReading(ctx, num(store.machineValue('brew.setpoint')));
  const current = reading.temp;
  const target = reading.target;
  const inStandby = reading.standby;
  const tone = current === undefined || inStandby ? 'idle' : (readyTone(ctx, current, target) ?? 'idle');
  // ready / heating / cooling as the display says it, the same as the chip over the other tabs (core/screens/ready.ts);
  // every other state with the local app's wording
  // (Spülen 2/3, Backflush 1/5, Entkalken 3/8, Standby, alarms; core/screens chipView)
  // offline, restarting, unknown: the ring says so instead of a state the machine may no longer be in
  const stateLabel = !view.info
    ? ''
    : reading.state !== READY_OR_HEATING_STATE ||
        conn.presence === 'offline' ||
        conn.presence === 'restarting' ||
        conn.presence === 'unknown'
      ? chipView(ctx).text
      : current !== undefined && tone !== 'idle'
        ? t(lang, tone === 'ready' ? 'stateReady' : tone === 'heat' ? 'stateHeating' : 'stateCooling')
        : t(lang, 'stateOn');
  const line = ringLine(ctx, reading, tone, history.samples);
  // the ready moment of the display: once after it warmed up from more than 5 K below, when it turns green
  const readyPulse = useReadyPulse(current, target, tone);
  // the heater's bar and the scale's symbol in the opening of the ring, as on the display (live heaterPower, scale)
  const live = liveOf(view, ctx.now);
  const heater = reading.look === 'live' && typeof live?.heaterPower === 'number' ? live.heaterPower : null;
  const scaleMark =
    live?.scale === 2 ? 'connected' : live?.scale === 1 ? 'waiting' : live ? ('off' as const) : null;

  // Feedback (Dominik, 19.10.2026: no "wird gesendet" under the buttons): the button shows that its command is on its
  // way (spinner, locked, AD-12), the result is the state itself (ring STANDBY, SPÜLEN), errors come as the message
  // above the tab bar (CommandToast) in plain words.
  const toggleStandby = () =>
    void client.send(machineId, { kind: 'action', action: 'standby.set', args: { on: !inStandby } });
  const startFlush = () => {
    setConfirming(false);
    void client.send(machineId, { kind: 'action', action: 'flush.start', confirm: true });
  };

  const flushLocked = client.commands.isLocked(machineId, 'flush.start');
  const standbyLocked = client.commands.isLocked(machineId, 'standby.set');
  const brewing = shotScreen !== null && shotScreen.phase !== 'done';
  const done = shotScreen !== null && shotScreen.phase === 'done';

  // Standby and switching on, a shot starting and ending swap the ring's words, the buttons and the cards below at
  // once: they fade in instead of jumping in one frame (Dominik 22.10.2026: "nicht flüssig genug"). Set before the
  // paint, so the new content never shows a frame at full strength first.
  const screenRef = useRef<HTMLElement>(null);
  const look = brewing ? 'shot' : done ? 'done' : activity ? 'water' : inStandby ? 'standby' : 'on';
  const shownLook = useRef(look);
  useLayoutEffect(() => {
    const el = screenRef.current;
    if (!el || shownLook.current === look) return;
    shownLook.current = look;
    el.dataset.swap = '';
    const timer = window.setTimeout(() => delete el.dataset.swap, SWAP_MS);
    return () => {
      window.clearTimeout(timer);
      delete el.dataset.swap;
    };
  }, [look]);

  return (
    <section
      ref={screenRef}
      className="screen machine"
      data-testid={`machine-${machineId}`}
      aria-busy={commands > 0}
    >
      <TopBar
        title={view.info?.name ?? machineId}
        sub={
          <ConnChip tone={conn.tone} data-testid="conn-state" data-presence={conn.presence}>
            {conn.text}
          </ConnChip>
        }
        right={accountButton}
      />
      {switcher}

      {/* after a data loss or a continued coupling: the last known state, offered once (FR-52, no backups) */}
      <RecoveryOffer />

      <FrameBanners />
      {/* the empty tank is the ring's own screen, as on the display: no banner saying it again */}
      {alarm && activity?.kind !== 'tank' && (
        <Banner tone={alarm.tone === 'error' ? 'danger' : 'warn'} icon="alert" data-testid="alarm">
          <strong>{alarm.text}</strong> {alarm.hint}
        </Banner>
      )}

      {shotScreen ? (
        <LiveShot screen={shotScreen} testId="live-shot" />
      ) : activity ? (
        <div className="machine-gauge-block">
          <ActivityDial testId="activity" />
        </div>
      ) : (
        <div className="machine-gauge-block">
          {/* standby closes the iris over the ring, switching on opens it while the temperature counts up from
              zero, as on the display (Iris.tsx) */}
          <Iris closed={inStandby}>
            {(reveal) => {
              const shown = current !== undefined && reveal !== null ? current * reveal : current;
              return (
                <div className="machine-gauge" data-look={reading.look}>
                  {/* the same three lines in every state: a gap changes colour and words, never the layout */}
                  <Gauge
                    temperature={shown}
                    setpoint={target}
                    tone={tone}
                    top={ringTop(ctx, reading, tone)}
                    follow={reveal !== null ? 'frame' : 'sample'}
                    pulse={readyPulse}
                    heater={heater}
                    scale={scaleMark}
                    className={reading.look === 'aging' ? 'is-stale' : undefined}
                  >
                    <span className="gauge-state" data-testid="machine-state">
                      {stateLabel}
                    </span>
                    <span
                      className={shown !== undefined ? 'gauge-temp' : 'gauge-temp is-empty'}
                      data-testid="temperature"
                    >
                      {shown !== undefined ? (
                        <>
                          {formatNumber(lang, shown, 1)}
                          <span className="gauge-unit">°C</span>
                        </>
                      ) : (
                        <span aria-hidden="true">–</span>
                      )}
                    </span>
                    <span className="gauge-sub" data-testid="gauge-sub">
                      {line}
                    </span>
                  </Gauge>
                </div>
              );
            }}
          </Iris>
        </div>
      )}

      {!shotScreen && (
        <div className="quick" role="group" aria-label={t(lang, 'quickActions')}>
          {/* in standby, switching on is what is due: the one filled button (prototype "Maschine: Standby") */}
          <Button
            variant={inStandby ? 'primary' : 'secondary'}
            size="sm"
            icon={inStandby ? 'power' : 'moon'}
            busy={standbyLocked}
            disabled={!controllable || standbyLocked}
            onClick={toggleStandby}
            data-testid="standby-toggle"
          >
            {inStandby ? t(lang, 'standbyOff') : t(lang, 'standbyOn')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon="drop"
            busy={flushLocked}
            disabled={!controllable || flushLocked || inStandby || activity !== null}
            onClick={() => setConfirming(true)}
            data-testid="flush"
          >
            {t(lang, 'flush')}
          </Button>
        </div>
      )}

      {/* after a shot the rating comes first (prototype "Bezug fertig"), the notes (rinse, drip tray) after it */}
      {done && <DoneCards onOpenShot={onOpenShot} />}

      {!brewing &&
        notes.filter((n) => !ringShowsNote(activity, n.key)).map((n) => <NoteRow key={n.key} note={n} />)}

      {!shotScreen && inStandby && (
        <StandbyCards onOpenHistory={onOpenHistory} onOpenSchedule={onOpenSchedule} />
      )}

      {!shotScreen && !inStandby && (
        <>
          {/* the next shot at a glance while it heats up (board "Maschine: heizt auf"); once it is ready the boards
              show the notes and the last shot right under the ring (23.10.2026). The tile opens the recipe page. */}
          {tone === 'heat' && (
            <Card className="recipe" data-testid="recipe">
              <CardHeader title={t(lang, 'sShot')} />
              <RecipeGlance showPreset onOpen={onOpenRecipe} openTestId="recipe-open" />
            </Card>
          )}

          {last?.shot && (
            <Card className="last-shot" data-testid="last-shot">
              <CardHeader
                title={last.title}
                action={
                  onOpenShot && (
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => last.shot && onOpenShot(last.shot.id)}
                      data-testid="last-shot-open"
                    >
                      {last.shot.when}
                      <Icon name="chevronRight" size="sm" />
                    </button>
                  )
                }
              />
              <LastShotFigures />
              {last.shot.channeling && <Banner tone="warn">{last.shot.channeling}</Banner>}
              <RateRow
                label={last.shot.taste.question}
                options={last.shot.taste.options}
                onPick={(v) => {
                  const id = last.shot?.id;
                  const now = shots.items[0]?.rating ?? null;
                  if (id) void client.shots(machineId).rate(id, tasteAfterTap(now, v));
                }}
                testId="taste"
              />
            </Card>
          )}
        </>
      )}

      {!brewing && (
        <Card className="history-card" data-testid="history-card">
          {hist.empty ? (
            <>
              <CardHeader title={hist.title} />
              <p className="hint">{hist.empty}</p>
            </>
          ) : (
            <LaneChart
              view={historyLanes(ctx, hist.samples, hist.from, hist.to)}
              info={{ text: ctx.tr('scrubHint'), label: ctx.tr('infoShow') }}
              testId="history-chart"
            />
          )}
        </Card>
      )}

      {/* the same question as the flush of Wartung (one question per action, 23.10.2026) */}
      {confirming && (
        <AskSheet
          ask={{
            title: t(lang, 'flushConfirmTitle'),
            text: t(lang, 'flushConfirm'),
            ok: t(lang, 'flushGo'),
            danger: false,
          }}
          titleId={`flush-title-${machineId}`}
          onYes={startFlush}
          onNo={() => setConfirming(false)}
          testId="flush"
        />
      )}
    </section>
  );
}

/** "28 s  36,8 g  wie üblich" (prototype "Letzter Bezug"): whole seconds, the weight, how it compares. */
function LastShotFigures() {
  const { ctx, client, machineId } = useMachine();
  const shots = useStore(client.shots(machineId));
  const shot = shots.items[0];
  if (!shot) return null;
  const line = shotLine(ctx, shot);
  const usual = usualText(ctx, usualCheck(shot, shots.items));
  return (
    <p className="shot-figures num" data-testid="last-shot-figures">
      <b data-testid="last-shot-time">{line.timeShort}</b>
      {line.weightText && <b>{line.weightText}</b>}
      {usual && (
        <span className={usual.off ? 'shot-usual is-off' : 'shot-usual'} data-testid="last-shot-usual">
          {usual.text}
        </span>
      )}
    </p>
  );
}

/** In standby (prototype "Maschine: Standby"): the day so far with a link to the history, the schedule as a row. */
function StandbyCards({
  onOpenHistory,
  onOpenSchedule,
}: {
  onOpenHistory?: () => void;
  onOpenSchedule?: () => void;
}) {
  const { ctx, client, machineId } = useMachine();
  const shots = useStore(client.shots(machineId));
  const day = todayCard(ctx, shots);
  const plan = scheduleRow(ctx);
  return (
    <>
      <Card className="today" data-testid="today-card">
        <CardHeader
          title={day.title}
          action={
            onOpenHistory && (
              <button type="button" className="link-btn" onClick={onOpenHistory} data-testid="today-history">
                {ctx.tr('historyLink')}
                <Icon name="chevronRight" size="sm" />
              </button>
            )
          }
        />
        <FactRow items={day.facts.map((f) => ({ ...f, testId: `today-${f.key}` }))} />
      </Card>
      {onOpenSchedule && (
        <Card className="nav-card" data-testid="standby-schedule">
          <NavList
            items={[
              {
                key: 'schedule',
                icon: 'clock',
                label: plan.label,
                // one line, the later days drop out first (WRITING.md 0a)
                hint: <FitLine parts={scheduleSummaryParts(ctx)} />,
                onClick: onOpenSchedule,
                testId: 'standby-schedule-open',
              },
            ]}
          />
        </Card>
      )}
    </>
  );
}

/**
 * After a shot (prototype "Bezug fertig"): the rating over the whole width and what to change for the next one, then
 * the shot's page with its curve, tags and note. With a Claude key a sour or bitter rating asks Claude, whose card takes
 * the place of the app's own tip (Dominik 22.10.2026); with the switch off or another rating the tip stays alone
 * (Dominik 23.10.2026: no "Vorschlag holen" under it). Without a key one quiet line after a rating offers to set it up,
 * until "Nicht jetzt".
 */
function DoneCards({ onOpenShot }: { onOpenShot?: (id: string) => void }) {
  const { ctx, client, machineId, send } = useMachine();
  const { assistant, view: claude } = useAssistant();
  const openClaude = useOpenClaudeSettings();
  const shots = useStore(client.shots(machineId));
  const shot = justBrewed(ctx, shots);
  // after "übernehmen" the recipe has the new size: the box says so instead of suggesting the next step
  const [applied, setApplied] = useState<string | null>(null);
  const rating = shot?.rating === 1 || shot?.rating === 2 || shot?.rating === 3 ? shot.rating : null;
  const tip = rating ? grindTip(ctx, rating) : null;
  const c = useClaudeCard(shot, 'done');
  const state = c?.card.state ?? 'none';
  // Claude speaks (thinking, its suggestion, a failure): the app's own tip steps back
  const claudeOn = state !== 'none';
  const offer = !claudeOn && setupOfferShown(claude, rating);
  return (
    <>
      <Card className="done-taste" data-testid="done-taste">
        <CardHeader title={ctx.tr('tasteQ')} />
        <Segmented
          label={ctx.tr('tasteQ')}
          options={([1, 2, 3] as const).map((value) => ({
            value,
            label: ctx.tr(value === 1 ? 'sour' : value === 2 ? 'good' : 'bitter'),
            on: rating === value,
          }))}
          allowNone
          disabled={!shot}
          onPick={(v) => {
            setApplied(null);
            if (!shot) return;
            const next = tasteAfterTap(rating, v);
            const saving = client.shots(machineId).rate(shot.id, next);
            assistant.rated(machineId, shot.id, next, ctx.lang, saving);
          }}
          testId="done-rate"
        />
        <Collapse open={!claudeOn && (applied !== null || !!tip)}>
          <div className="tip-box" data-testid="grind-tip">
            <p>{applied ?? tip?.text}</p>
            {applied === null && tip?.apply && tip.applyLabel && (
              <Button
                variant="secondary"
                icon="check"
                block
                onClick={() => {
                  if (!tip.apply) return;
                  setApplied(tip.doneText);
                  void send(tip.apply);
                }}
                data-testid="grind-tip-apply"
              >
                {tip.applyLabel}
              </Button>
            )}
          </div>
        </Collapse>
        <Collapse open={state !== 'none'}>{c && state !== 'none' && <ClaudeBox c={c} />}</Collapse>
        <Collapse open={offer}>
          <div className="ai-offer" data-testid="claude-offer">
            <p className="ai-offer-text">
              <Icon name="sparkle" size="sm" className="ai-box-mark" />
              <span>{ctx.tr('aiSetup')}</span>
            </p>
            <div className="ai-box-actions">
              {openClaude && (
                <button type="button" className="link-btn" onClick={openClaude} data-testid="claude-offer-go">
                  {ctx.tr('aiSetupGo')}
                </button>
              )}
              <button
                type="button"
                className="link-btn dim"
                onClick={() => void assistant.setSettings({ setupDismissed: true })}
                data-testid="claude-offer-later"
              >
                {ctx.tr('aiSetupLater')}
              </button>
            </div>
          </div>
        </Collapse>
      </Card>
      {shot && onOpenShot && (
        <Card className="nav-card" data-testid="done-curve">
          <NavList
            items={[
              {
                key: 'curve',
                icon: 'curve',
                label: ctx.tr('curveRow'),
                hint: ctx.tr('curveRowHint'),
                onClick: () => onOpenShot(shot.id),
                testId: 'done-curve-open',
              },
            ]}
          />
        </Card>
      )}
    </>
  );
}
